import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export const useDatabasePopupStore = defineStore('databasePopup', () => {
  const popupTables = ref([])
  let nextPopupId = 1

  // Load state from localStorage on initialization
  const loadFromLocalStorage = () => {
    try {
      const savedState = localStorage.getItem('databasePopupState')
      if (savedState) {
        const parsed = JSON.parse(savedState)
        if (parsed.popupTables && Array.isArray(parsed.popupTables)) {
          popupTables.value = parsed.popupTables
          // Update nextPopupId to avoid conflicts
          const maxId = parsed.popupTables.reduce((max, popup) => {
            const idNum = parseInt(popup.id.replace('popup-', ''))
            return Math.max(max, idNum)
          }, 0)
          nextPopupId = maxId + 1
        }
        if (parsed.nextPopupId) {
          nextPopupId = Math.max(nextPopupId, parsed.nextPopupId)
        }
      }
    } catch (error) {
      console.warn('Failed to load popup state from localStorage:', error)
    }
  }

  // Save state to localStorage
  const saveToLocalStorage = () => {
    try {
      const stateToSave = {
        popupTables: popupTables.value,
        nextPopupId
      }
      localStorage.setItem('databasePopupState', JSON.stringify(stateToSave))
    } catch (error) {
      console.warn('Failed to save popup state to localStorage:', error)
    }
  }

  // Watch for changes and save to localStorage
  watch(popupTables, () => {
    saveToLocalStorage()
  }, { deep: true })

  // Initialize from localStorage
  loadFromLocalStorage()

  // Drag and resize state
  const dragState = ref({
    isDragging: false,
    isResizing: false,
    popupId: null,
    startX: 0,
    startY: 0,
    startPopupX: 0,
    startPopupY: 0,
    startWidth: 0,
    startHeight: 0
  })

  // Add a new popup table
  const addPopupTable = (tableName, type = 'data') => {
    // Only check for existing popup if tableName is provided
    if (tableName) {
      const existingPopup = popupTables.value.find(p => p.tableName === tableName && p.type === type)
      if (existingPopup) {
        // Bring existing popup to front
        existingPopup.show = true
        existingPopup.minimized = false
        return existingPopup.id
      }
    }

    const popupId = `popup-${nextPopupId++}`
    const title = tableName 
      ? `${tableName} - ${type === 'structure' ? 'Structure' : 'Data'}`
      : 'Database Manager'
    
    // Calculate center position
    const popupWidth = 800
    const popupHeight = 600
    const centerX = Math.max(0, (window.innerWidth - popupWidth) / 2)
    const centerY = Math.max(0, (window.innerHeight - popupHeight) / 2)
    
    // Add small offset for multiple popups to avoid complete overlap
    const offset = popupTables.value.length * 30
    
    const newPopup = {
      id: popupId,
      tableName: tableName || '',
      type,
      title,
      show: true,
      minimized: false,
      maximized: false,
      chatMode: false,
      x: centerX + offset,
      y: centerY + offset,
      width: popupWidth,
      height: popupHeight,
      restoreX: 0,
      restoreY: 0,
      restoreWidth: 0,
      restoreHeight: 0
    }

    popupTables.value.push(newPopup)
    return popupId
  }

  // Close a popup
  const closePopup = (popupId) => {
    const index = popupTables.value.findIndex(p => p.id === popupId)
    if (index !== -1) {
      popupTables.value.splice(index, 1)
    }
  }

  // Minimize/restore popup
  const minimizePopup = (popupId) => {
    const popup = popupTables.value.find(p => p.id === popupId)
    if (popup) {
      popup.minimized = !popup.minimized
    }
  }

  // Maximize/restore popup
  const toggleMaximizePopup = (popupId) => {
    const popup = popupTables.value.find(p => p.id === popupId)
    if (popup) {
      if (popup.maximized) {
        // Restore
        popup.maximized = false
        popup.x = popup.restoreX || 100
        popup.y = popup.restoreY || 100
        popup.width = popup.restoreWidth || 800
        popup.height = popup.restoreHeight || 600
      } else {
        // Maximize
        popup.restoreX = popup.x
        popup.restoreY = popup.y
        popup.restoreWidth = popup.width
        popup.restoreHeight = popup.height
        popup.maximized = true
        popup.x = 0
        popup.y = 0
        popup.width = window.innerWidth
        popup.height = window.innerHeight
      }
    }
  }

  // Drag functionality
  function startDrag(event, popupId) {
    const popup = popupTables.value.find(p => p.id === popupId)
    if (!popup || popup.maximized) return
    
    dragState.value = {
      isDragging: true,
      isResizing: false,
      popupId: popupId,
      startX: event.clientX,
      startY: event.clientY,
      startPopupX: popup.x,
      startPopupY: popup.y,
      startWidth: 0,
      startHeight: 0
    }
    
    document.addEventListener('mousemove', handleDrag)
    document.addEventListener('mouseup', stopDrag)
    event.preventDefault()
  }

  function startResize(event, popupId) {
    const popup = popupTables.value.find(p => p.id === popupId)
    if (!popup || popup.maximized) return
    
    dragState.value = {
      isDragging: false,
      isResizing: true,
      popupId: popupId,
      startX: event.clientX,
      startY: event.clientY,
      startPopupX: 0,
      startPopupY: 0,
      startWidth: popup.width,
      startHeight: popup.height
    }
    
    document.addEventListener('mousemove', handleResize)
    document.addEventListener('mouseup', stopResize)
    event.preventDefault()
    event.stopPropagation()
  }

  function handleDrag(event) {
    if (!dragState.value.isDragging) return
    
    const popup = popupTables.value.find(p => p.id === dragState.value.popupId)
    if (!popup) return
    
    const deltaX = event.clientX - dragState.value.startX
    const deltaY = event.clientY - dragState.value.startY
    
    popup.x = Math.max(0, dragState.value.startPopupX + deltaX)
    popup.y = Math.max(0, dragState.value.startPopupY + deltaY)
  }

  function handleResize(event) {
    if (!dragState.value.isResizing) return
    
    const popup = popupTables.value.find(p => p.id === dragState.value.popupId)
    if (!popup) return
    
    const deltaX = event.clientX - dragState.value.startX
    const deltaY = event.clientY - dragState.value.startY
    
    popup.width = Math.max(400, dragState.value.startWidth + deltaX)
    popup.height = Math.max(300, dragState.value.startHeight + deltaY)
  }

  function stopDrag() {
    dragState.value.isDragging = false
    document.removeEventListener('mousemove', handleDrag)
    document.removeEventListener('mouseup', stopDrag)
  }

  function stopResize() {
    dragState.value.isResizing = false
    document.removeEventListener('mousemove', handleResize)
    document.removeEventListener('mouseup', stopResize)
  }

  // Close all popups
  const closeAllPopups = () => {
    popupTables.value = []
  }

  // Toggle visibility of all popups
  const allPopupsHidden = ref(false)
  
  const toggleAllPopupsVisibility = () => {
    allPopupsHidden.value = !allPopupsHidden.value
    popupTables.value.forEach(popup => {
      popup.show = !allPopupsHidden.value
    })
  }

  const getVisiblePopupsCount = () => {
    return popupTables.value.filter(popup => popup.show).length
  }

  // Toggle chat mode for popup
  const toggleChatMode = (popupId) => {
    const popup = popupTables.value.find(p => p.id === popupId)
    if (!popup) return

    if (!popup.chatMode) {
      // Enter chat mode - save current position and resize
      popup.restoreX = popup.x
      popup.restoreY = popup.y
      popup.restoreWidth = popup.width
      popup.restoreHeight = popup.height
      popup.maximized = false

      // Position at bottom right like a chat window
      popup.chatMode = true
      popup.width = 650
      popup.height = 500
      
      // Calculate position based on how many chat mode popups already exist
      repositionChatModePopups()
    } else {
      // Exit chat mode - restore original position
      popup.chatMode = false
      popup.x = popup.restoreX || 100
      popup.y = popup.restoreY || 100
      popup.width = popup.restoreWidth || 800
      popup.height = popup.restoreHeight || 600
      
      // Reposition remaining chat mode popups
      repositionChatModePopups()
    }
  }

  // Reposition all chat mode popups side by side
  const repositionChatModePopups = () => {
    const chatModePopups = popupTables.value.filter(p => p.chatMode)
    const chatWidth = 650
    const chatHeight = 500
    const spacing = 10
    const rightMargin = 20

    chatModePopups.forEach((popup, index) => {
      // Position from right to left
      popup.x = window.innerWidth - (chatWidth * (index + 1)) - (spacing * index) - rightMargin
      popup.y = window.innerHeight - chatHeight - 20
      popup.width = chatWidth
      popup.height = chatHeight
    })
  }

  // Keyboard shortcut handler with debounce
  let keyboardDebounce = false

  const handleKeyDown = (event) => {
    // Cmd+Shift+D (Mac) or Ctrl+Shift+D (Windows/Linux) to show all popups and create new one
    if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'D') {
      event.preventDefault()
      
      // Debounce to prevent multiple triggers
      if (keyboardDebounce) return
      keyboardDebounce = true
      
      // If popups are hidden, show them all
      if (allPopupsHidden.value) {
        toggleAllPopupsVisibility()
      }
      
      // Always create a new popup when using the shortcut
      addPopupTable('', 'data')
      
      // Reset debounce after 500ms
      setTimeout(() => {
        keyboardDebounce = false
      }, 500)
    }
  }

  // Initialize keyboard listener once
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', handleKeyDown)
  }

  // Clear localStorage
  const clearStoredState = () => {
    try {
      localStorage.removeItem('databasePopupState')
      localStorage.removeItem('databasePopupUIState')
    } catch (error) {
      console.warn('Failed to clear stored state:', error)
    }
  }

  // Database authentication state
  const isAuthenticated = ref(false)
  const authToken = ref(null)

  // Check authentication status
  const checkAuthentication = () => {
    const token = sessionStorage.getItem('database_auth_token')
    const expires = sessionStorage.getItem('database_auth_expires')
    
    if (token && expires && Date.now() < parseInt(expires)) {
      isAuthenticated.value = true
      authToken.value = token
      return true
    }
    
    // Clear expired auth
    sessionStorage.removeItem('database_auth_token')
    sessionStorage.removeItem('database_auth_expires')
    isAuthenticated.value = false
    authToken.value = null
    return false
  }

  // Set authentication
  const setAuthentication = (token, remember = false) => {
    isAuthenticated.value = true
    authToken.value = token
    
    if (remember) {
      sessionStorage.setItem('database_auth_token', token)
      sessionStorage.setItem('database_auth_expires', Date.now() + (4 * 60 * 60 * 1000)) // 4 hours
    }
  }

  // Clear authentication
  const clearAuthentication = () => {
    isAuthenticated.value = false
    authToken.value = null
    sessionStorage.removeItem('database_auth_token')
    sessionStorage.removeItem('database_auth_expires')
  }

  return {
    popupTables,
    dragState,
    allPopupsHidden,
    addPopupTable,
    closePopup,
    minimizePopup,
    toggleMaximizePopup,
    startDrag,
    startResize,
    handleDrag,
    handleResize,
    stopDrag,
    stopResize,
    closeAllPopups,
    toggleAllPopupsVisibility,
    getVisiblePopupsCount,
    toggleChatMode,
    saveToLocalStorage,
    loadFromLocalStorage,
    clearStoredState,
    isAuthenticated,
    authToken,
    checkAuthentication,
    setAuthentication,
    clearAuthentication
  }
}) 